import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuItem } from 'primeng/api';
import { StepperModule } from 'primeng/stepper';
import { LanguagesService } from '../services/languages.service';

interface Step {
    order: number;
    name: string;
}

@Component({
    selector: 'platform-stepper',
    standalone: true,
    imports: [StepperModule],
    template: `
        <div class="p-step-container">
            <p-stepper class="custom-steps">
                @for (item of stepItems; track $index) {
                    <p-stepperPanel>
                        <ng-template pTemplate="header" let-index="index">
                            @if (index < activeStepIndex) {
                                <i
                                    class="pi pi-check p-steps-number bg-primary-700 text-gray-50"
                                    style="font-size: 0.75rem;"
                                ></i>
                            } @else {
                                <span
                                    class="p-steps-number font-semibold"
                                    [class.bg-primary-700]="index === activeStepIndex"
                                    [class.text-gray-50]="index === activeStepIndex"
                                    >{{ index + 1 }}
                                </span>
                            }
                            <span
                                class="font-medium"
                                [class.text-primary-700]="index < activeStepIndex"
                                [class.text-gray-300]="index >= activeStepIndex"
                                >{{ item.label }}</span
                            >
                        </ng-template>
                    </p-stepperPanel>
                }
            </p-stepper>
        </div>
    `,
    styles: [
        `
            .p-step-container {
                width: fit-content !important;
            }
            .custom-steps .p-steps-item .p-menuitem-link {
                display: flex;
                align-items: center;
                color: #1f3e72;
                font-weight: 500;
                font-size: 1rem;
            }

            .custom-steps .p-steps-number {
                border-radius: 50%;
                width: 1.714rem;
                height: 1.714rem;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-inline: 0.5rem;
                padding: 0.286rem;
                border: 1px solid var(--alpha-primary-inactive-color);
            }

            .custom-steps .p-steps-item.p-highlight .p-steps-number {
                background-color: #265ebe !important;
            }

            .custom-steps {
                margin: 0 auto;
                width: 270px !important;
            }
        `,
    ],
})
export class StepperComponent implements OnChanges {
    @Input() currentStepName: string = '';
    @Input() steps: Step[] = [];
    languagesService = inject(LanguagesService);

    stepItems: MenuItem[] = [];
    activeStepIndex: number = 0;

    constructor() {
        this.languagesService.selectedLanguage$.pipe(takeUntilDestroyed()).subscribe((language) => {
            this.initializeSteps();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['steps'] || changes['currentStepName']) {
            this.initializeSteps();
        }
    }

    private initializeSteps() {
        const sortedSteps = [...this.steps].sort((a, b) => a.order - b.order);

        // Map sorted steps to PrimeNG MenuItems
        this.stepItems = sortedSteps.map((step) => ({
            label: this.languagesService.getTranslation(step.name) || step.name,
        }));

        // Set the active step index based on the current step name
        this.activeStepIndex = sortedSteps.findIndex((step) => step.name === this.currentStepName);
    }
}
